/**
 * EPCS Activation Watcher
 *
 * Global hook that polls for dosespot_onboarding_status changes.
 * When the provider completes IDP and has EPCS requested, the status
 * transitions from "idp_needed" to "tfa_needed". This hook detects
 * that transition and shows a toast prompting TFA setup with a button
 * to open DoseSpot directly.
 *
 * Self-gates: only polls when user is registered (has started onboarding).
 */

import { useCallback, useEffect, useRef } from 'react';
import { Button } from '@mui/material';
import { enhancedApi as generatedUsersApi } from '@store/generated/users';
import { useSnackbar } from 'notistack';

import { useLazyGetClinicianSsoQuery } from '../store';
import { useEPrescriptionStatus } from '../store/hooks';

export function useEpcsActivationWatcher() {
  const { enqueueSnackbar } = useSnackbar();
  const { isUserConnected } = useEPrescriptionStatus();
  const [getClinicianSso] = useLazyGetClinicianSsoQuery();

  // Poll the v2 user-info endpoint to detect onboarding status changes.
  // Only poll when the user is registered and hasn't completed onboarding.
  const { data } = generatedUsersApi.useGetV2ProviderUserInfoQuery(undefined, {
    pollingInterval: isUserConnected ? 30_000 : 0,
  });

  // Field exists at runtime but is not yet in the generated OpenAPI types
  const dosespotOnboardingStatus =
    (data as { dosespot_onboarding_status?: string | null } | undefined)
      ?.dosespot_onboarding_status ?? null;

  // Track previous status to detect transitions.
  // Initialized to `undefined` so we don't fire on first mount.
  const prevStatusRef = useRef<string | null | undefined>(undefined);

  const handleOpenDoseSpot = useCallback(async () => {
    const ssoWindow = window.open('about:blank', '_blank');
    if (!ssoWindow) return; // Popup blocked — user can still open from Account Settings
    try {
      const result = await getClinicianSso().unwrap();
      if (result.sso_url) {
        ssoWindow.opener = null;
        ssoWindow.location.href = result.sso_url;
      } else {
        ssoWindow.close();
      }
    } catch {
      ssoWindow.close();
      // SSO failure is non-critical here — user can still open from Account Settings
    }
  }, [getClinicianSso]);

  useEffect(() => {
    // Skip until we've seen the first value
    if (prevStatusRef.current === undefined) {
      prevStatusRef.current = dosespotOnboardingStatus;
      return;
    }

    // Detect the "idp_needed" → "tfa_needed" transition
    // This means IDP is done and TFA setup is the next step
    if (
      prevStatusRef.current === 'idp_needed' &&
      dosespotOnboardingStatus === 'tfa_needed'
    ) {
      enqueueSnackbar(
        'Identity proofing complete — open DoseSpot to set up TFA (two-factor authentication).',
        {
          variant: 'success',
          autoHideDuration: 15_000,
          preventDuplicate: true,
          action: _snackbarId => (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              sx={{ fontWeight: 600, textTransform: 'none' }}
              onClick={() => {
                void handleOpenDoseSpot();
              }}
            >
              Open DoseSpot
            </Button>
          ),
        },
      );
    }

    prevStatusRef.current = dosespotOnboardingStatus;
  }, [dosespotOnboardingStatus, enqueueSnackbar, handleOpenDoseSpot]);
}
